import React from "react";
import { Tooltip } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  CON_MIGRATION_DUPLICATE_SOURCE_BATCH,
  CON_MIGRATION_DUPLICATE_SOURCE_SYSTEM,
} from "../../utils/sys/Constants.tsx";
import {
  MSG_ALREADY_EXISTS_IN_SYSTEM,
  MSG_DUPLICATE_IN_IMPORT_FILE,
  MSG_ITEM_RECORD_EXISTS,
} from "../../utils/sys/Messages.tsx";

export type DuplicateSourceFlag = "batch" | "system" | typeof CON_MIGRATION_DUPLICATE_SOURCE_BATCH | typeof CON_MIGRATION_DUPLICATE_SOURCE_SYSTEM | string | null | undefined;

export const normalizeDuplicateSource = (
  source?: DuplicateSourceFlag
): "batch" | "system" | null => {
  if (!source) return null;
  const normalized = String(source).trim().toUpperCase();
  if (normalized === "SYSTEM" || normalized === CON_MIGRATION_DUPLICATE_SOURCE_SYSTEM) {
    return "system";
  }
  if (normalized === "BATCH" || normalized === CON_MIGRATION_DUPLICATE_SOURCE_BATCH) {
    return "batch";
  }
  return null;
};

export const getDuplicateStatusMessage = (source?: DuplicateSourceFlag): string => {
  const normalized = normalizeDuplicateSource(source);
  if (normalized === "system") return MSG_ALREADY_EXISTS_IN_SYSTEM;
  if (normalized === "batch") return MSG_DUPLICATE_IN_IMPORT_FILE;
  return MSG_ITEM_RECORD_EXISTS;
};

const extractValidationErrorText = (validationerrors?: string | null): string => {
  if (!validationerrors) return "";
  try {
    const parsed = JSON.parse(validationerrors);
    if (Array.isArray(parsed)) return parsed.map(String).join(" ");
    return String(parsed);
  } catch {
    return validationerrors;
  }
};

/** Resolve batch vs system when API omits duplicatesource (uses validationerrors text). */
export const resolveDuplicateSourceFromRow = (row: {
  duplicatesource?: DuplicateSourceFlag;
  validationerrors?: string | null;
}): "batch" | "system" => {
  const fromField = normalizeDuplicateSource(row.duplicatesource);
  if (fromField) return fromField;

  const text = extractValidationErrorText(row.validationerrors).toLowerCase();
  if (text.includes(MSG_ALREADY_EXISTS_IN_SYSTEM.toLowerCase())) return "system";
  if (text.includes(MSG_DUPLICATE_IN_IMPORT_FILE.toLowerCase())) return "batch";
  return "batch";
};

export const getDuplicateStatusMessageForRow = (row: {
  duplicatesource?: DuplicateSourceFlag;
  validationerrors?: string | null;
}): string => getDuplicateStatusMessage(resolveDuplicateSourceFromRow(row));

export const renderImportStatusIcon = (
  status?: string,
  duplicateSource?: DuplicateSourceFlag,
  showTooltip = true,
  validationerrors?: string | null
): React.ReactElement => {
  if (status === "VALID") return <CheckCircleIcon color="success" fontSize="small" />;
  if (status === "INVALID") return <WarningAmberIcon color="warning" fontSize="small" />;

  if (status === "DUPLICATE") {
    const source = resolveDuplicateSourceFromRow({
      duplicatesource: duplicateSource,
      validationerrors,
    });
    const icon =
      source === "system" ? (
        <ContentCopyIcon color="warning" fontSize="small" />
      ) : (
        <ContentCopyIcon color="info" fontSize="small" />
      );
    const title = getDuplicateStatusMessage(source);
    if (!showTooltip) return icon;
    return (
      <Tooltip title={title} arrow placement="top">
        {icon}
      </Tooltip>
    );
  }

  if (status === "UNPOSTED") return <CheckCircleIcon color="disabled" fontSize="small" />;
  return <CheckCircleIcon color="disabled" fontSize="small" />;
};
